import { useEffect, useLayoutEffect, useState } from 'react';

/**
 * Measured overlay for `MfaOtpInputs` (2026-09-25): ONE focus ring that glides
 * between cells, plus the exit ghosts for deleted digits. Purely decorative —
 * `aria-hidden`, `pointer-events: none`, absolutely positioned inside the
 * (position: relative) group, so it never takes part in the flex layout and
 * can't shift a cell. It reads the REAL cell boxes, so the
 * width-fit cells `MfaOtpRow` scales on narrow phones are honored.
 *
 * Until a cell has a measured width (first paint, jsdom) the parent keeps its
 * per-cell ring, so nothing ever renders ring-less.
 */
const GLIDE_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

// Sub-pixel boxes relative to the group's padding edge. Rect deltas are
// divided by the group's current scale, so a modal mid-enter (transform:
// scale) still measures layout-space pixels; offset* is the fallback.
function readRects(group, cells) {
  const g = group?.getBoundingClientRect?.();
  const scale = g && group.offsetWidth ? g.width / group.offsetWidth : 0;
  return cells.map((el) => {
    if (!el) return null;
    const r = el.getBoundingClientRect?.();
    if (!g || !r || !scale) {
      return { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight };
    }
    return {
      x: (r.left - g.left) / scale - group.clientLeft,
      y: (r.top - g.top) / scale - group.clientTop,
      w: r.width / scale,
      h: r.height / scale,
    };
  });
}

function sameRects(a, b) {
  if (!a || a.length !== b.length) return false;
  return a.every((r, i) => {
    const s = b[i];
    if (!r || !s) return r === s;
    return r.x === s.x && r.y === s.y && r.w === s.w && r.h === s.h;
  });
}

/** Cell boxes relative to the group; re-read on resize. `null` until known. */
export function useOtpCellRects(groupRef, cellRefs, inputH) {
  const [rects, setRects] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const next = readRects(groupRef.current, cellRefs.current.slice(0, 6));
      setRects((prev) => (sameRects(prev, next) ? prev : next));
    };
    measure();
    if (typeof window === 'undefined') return undefined;
    let ro = null;
    if (typeof window.ResizeObserver === 'function') {
      ro = new window.ResizeObserver(measure);
      if (groupRef.current) ro.observe(groupRef.current);
      cellRefs.current.forEach((el) => el && ro.observe(el));
    }
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [groupRef, cellRefs, inputH]);
  return rects;
}

export default function OtpMotionLayer({
  rects, focusedIndex, showRing, ringShadow, radius, reduced, ghosts, gen, font,
}) {
  const target = rects?.[focusedIndex];
  const glide = !!target && target.w > 0;
  // Arm the transition only after the ring has painted in place once, so it
  // never flies in from the group's origin on mount.
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!glide || armed) return undefined;
    const id = setTimeout(() => setArmed(true), 0);
    return () => clearTimeout(id);
  }, [glide, armed]);

  const ghostEntries = Object.entries(ghosts || {});
  if (!glide && !ghostEntries.length) return null;

  return (
    <>
      {glide && (
        <span
          aria-hidden="true"
          data-testid="mfa-otp-glide"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: target.w,
            height: target.h,
            transform: `translate(${target.x}px, ${target.y}px)`,
            borderRadius: radius,
            boxShadow: ringShadow,
            pointerEvents: 'none',
            opacity: showRing ? 1 : 0,
            transition: armed && !reduced
              ? `transform 160ms ${GLIDE_EASE}, width 160ms ${GLIDE_EASE}, opacity 140ms ${GLIDE_EASE}`
              : 'none',
          }}
        />
      )}
      {ghostEntries.map(([i, digit]) => {
        const r = rects?.[i];
        if (!r || !r.w) return null;
        return (
          <span
            key={`${gen}-${i}`}
            aria-hidden="true"
            className="cvx-otp-ghost-out"
            data-testid="mfa-otp-ghost"
            style={{
              position: 'absolute',
              left: r.x,
              top: r.y,
              width: r.w,
              height: r.h,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              ...font,
            }}
          >
            {digit}
          </span>
        );
      })}
    </>
  );
}
